// src/contexts/NavGuardContext.js
// ─────────────────────────────────────────────────────────────────────────────
// Navigation guard for unsaved form work.
//
// A form (e.g. DynamicFormRenderer) calls `setDirty(true)` while the user is
// filling it in and `setDirty(false)` once the draft is saved. Any navigation
// that would abandon the form — a sidebar link, the in-form Back button, Sign
// Out — is wrapped in `attemptNavigation(action)`: when there are unsaved
// changes we pop a single shared confirmation ("Are you sure you want to leave?
// Make sure you clicked Save Draft") and only run `action` if the user confirms.
// Hard browser navigation (refresh / tab close / browser back) is caught with a
// native `beforeunload` prompt.
//
// The app never blocks navigation silently — if the provider is missing (e.g.
// in a test), `attemptNavigation` simply runs the action immediately.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const NavGuardContext = createContext(null);

export function NavGuardProvider({ children }) {
  // Dirty is a REF, never state: it's only ever read on navigation (beforeunload
  // + attemptNavigation), so it must not re-render the tree. Marking dirty from a
  // form's input/change listener therefore has zero render cost and can't disturb
  // a controlled input mid-edit (which caused a "first selection ignored" bug).
  const dirtyRef        = useRef(false);
  // Pending navigation stashed while the confirm dialog is open. Stored inside an
  // object so a function value isn't mistaken for a state updater.
  const [pending, setPending] = useState(null);

  const setDirty = useCallback((value) => {
    dirtyRef.current = !!value;
  }, []);

  // Run `action` now when the form is clean; otherwise hold it behind the dialog.
  const attemptNavigation = useCallback((action) => {
    if (typeof action !== 'function') return;
    if (!dirtyRef.current) { action(); return; }
    setPending({ action });
  }, []);

  const handleConfirm = useCallback(() => {
    const action = pending?.action;
    setPending(null);
    setDirty(false);            // leaving with intent — drop the guard
    if (typeof action === 'function') action();
  }, [pending, setDirty]);

  const handleCancel = useCallback(() => setPending(null), []);

  // Warn on hard browser navigation (refresh / close / browser back) while dirty.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';       // required for Chrome to show the native prompt
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return (
    <NavGuardContext.Provider value={{ setDirty, attemptNavigation }}>
      {children}
      <ConfirmDialog
        open={!!pending}
        variant="warning"
        icon="⚠️"
        title="Leave without saving?"
        message={
          <>
            You have <b>unsaved changes</b> in this form. Please make sure you have
            clicked <b>“Save Draft”</b> before you leave — otherwise the details you
            entered will be lost.
          </>
        }
        confirmLabel="Leave anyway"
        cancelLabel="Stay on this page"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </NavGuardContext.Provider>
  );
}

// Safe default so consumers work even without a provider (navigation just runs).
const FALLBACK = { setDirty: () => {}, attemptNavigation: (fn) => { if (typeof fn === 'function') fn(); } };

export function useNavGuard() {
  return useContext(NavGuardContext) || FALLBACK;
}

export default NavGuardContext;
